import SunAccent from "@/components/feature/SunAccent";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { useKonaseemaGuide } from "@/hooks/usePublicGuide";

const exploreLinks = [
  {
    icon: "ri-plant-line",
    title: "Discover Farms",
    text: "Browse every farm that hosts visitors and see where each one sits on the map.",
    href: "/farms",
  },
  {
    icon: "ri-road-map-line",
    title: "Konaseema Guide",
    text: "Seasons, waterways, food and everything you should know before you arrive.",
    href: "/konaseema",
  },
  {
    icon: "ri-compass-3-line",
    title: "All Experiences",
    text: "Filter eight curated farm experiences by audience, season and duration.",
    href: "/experiences",
  },
  {
    icon: "ri-user-heart-line",
    title: "Meet Our Farmers",
    text: "Get to know the real hosts and the families behind each experience.",
    href: "/farmers",
  },
];

export default function TripKnowBefore() {
  const { seasons: konaseemaSeasons, reach: konaseemaReach } = useKonaseemaGuide();
  return (
    <section className="sun-glaze bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Before You Come
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            When to Visit &amp; How to Get Here
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Konaseema changes beautifully through the year. Here's a quick look at each season and
            the easiest ways to reach the delta.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-14 md:mb-20">
          {konaseemaSeasons.map((season, index) => (
            <Reveal key={season.name} delay={(index % 3) * 80} className="h-full">
              <div
                className={`h-full rounded-lg border p-6 md:p-7 flex flex-col ${
                  season.best ? "bg-accent-50 border-accent-300" : "bg-background-100 border-background-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`w-12 h-12 flex items-center justify-center rounded-full ${
                      season.best ? "bg-accent-500 text-background-50" : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <i className={`${season.icon} text-2xl`}></i>
                  </span>
                  {season.best && (
                    <span className="bg-accent-500 text-background-50 text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                      Best time
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-xl text-foreground-950 font-semibold mt-5">
                  {season.name}
                </h3>
                <p className="text-xs font-semibold uppercase tracking-wider text-accent-600 mt-1.5">
                  {season.months}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mt-3">{season.weather}</p>
                <p className="text-sm text-foreground-700 leading-relaxed mt-3">{season.highlights}</p>
                <p className="flex items-start gap-2 text-xs text-foreground-600 leading-relaxed mt-4 pt-4 border-t border-background-200">
                  <i className="ri-lightbulb-line text-accent-500 text-base shrink-0"></i>
                  {season.tip}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start mb-14 md:mb-20">
          <Reveal className="lg:col-span-5">
            <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
              Getting Here
            </h3>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">
              Konaseema is easy to reach by air, rail or road — and once you're here, boats and
              autos do the rest.
            </p>
            <Link
              to="/konaseema"
              className="mt-6 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Read the Konaseema Guide
              <i className="ri-arrow-right-line"></i>
            </Link>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {konaseemaReach.map((item) => (
                <div
                  key={item.mode}
                  className="bg-background-100 rounded-lg border border-background-200 p-5"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <p className="font-heading text-base text-foreground-950 font-semibold mt-4">
                    {item.mode}
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-2">{item.detail}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal className="max-w-3xl mb-8">
          <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
            Everything That Goes Into Your Plan
          </h3>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {exploreLinks.map((link, index) => (
            <Reveal key={link.title} delay={(index % 4) * 80} className="h-full">
              <Link
                to={link.href}
                className="group h-full flex flex-col bg-background-100 rounded-lg border border-background-200 p-6 hover:border-accent-300 transition-colors cursor-pointer"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${link.icon} text-2xl`}></i>
                </span>
                <h4 className="font-heading text-lg text-foreground-950 font-semibold mt-5">
                  {link.title}
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed mt-2 flex-1">
                  {link.text}
                </p>
                <span className="flex items-center gap-1 text-sm font-semibold text-primary-700 mt-4">
                  Open
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}